import { useEffect, useState } from "react";
import { createCourt, updateCourt } from "../../api/courts";
import { ICourt } from "../../models/ICourt";
import { Alert, Button, Dialog, Field, Input, Switch, Textarea } from "../../ui";
import "./styles.scss";

type CourtDialogProps = {
  court?: ICourt;
  isOpen: boolean;
  onClose: (update?: boolean) => void;
};

const CourtDialog = (props: CourtDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    isActive: true,
  });

  useEffect(() => {
    if (props.isOpen) {
      if (props.court) {
        setFormData({
          name: props.court.name,
          description: props.court.description || "",
          isActive: props.court.isActive,
        });
      } else {
        setFormData({
          name: "",
          description: "",
          isActive: true,
        });
      }
    }
  }, [props.isOpen, props.court]);

  const handleClose = (update?: boolean) => {
    props.onClose(update);
    setErrorMessage("");
  };

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.name.trim()) {
      setErrorMessage("Court name is required");
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      if (props.court) {
        // Update existing court
        await updateCourt(props.court.id, {
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          isActive: formData.isActive,
        });
      } else {
        // Create new court
        await createCourt({
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          isActive: formData.isActive,
        });
      }
      handleClose(true);
    } catch (error: any) {
      setErrorMessage(error?.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={props.isOpen}
      onClose={() => handleClose()}
      title={props.court ? "Edit Court" : "Create Court"}
      busy={loading}
      bodyClassName="court-dialog__body"
      footer={
        <>
          <Button variant="ghost" onClick={() => handleClose()} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={loading}>
            {props.court ? "Update" : "Create"}
          </Button>
        </>
      }
    >
      {errorMessage && <Alert tone="danger">{errorMessage}</Alert>}

      <Field label="Court Name" required>
        {(id) => (
          <Input
            id={id}
            type="text"
            placeholder="e.g., Court 1"
            value={formData.name}
            onChange={(e) => handleChange("name", e.target.value)}
            disabled={loading}
          />
        )}
      </Field>

      <Field label="Description">
        {(id) => (
          <Textarea
            id={id}
            placeholder="Optional description for this court"
            value={formData.description}
            onChange={(e) => handleChange("description", e.target.value)}
            rows={3}
            disabled={loading}
          />
        )}
      </Field>

      <Switch
        checked={formData.isActive}
        onChange={(checked) => handleChange("isActive", checked)}
        label="Active"
        disabled={loading}
      />
    </Dialog>
  );
};

export default CourtDialog;
